/**
 * Scene operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class SceneManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listScenes(
    projectUuid: string,
    page = 1,
    size = 20,
    name?: string,
    sceneType?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (name) options.name = name;
    if (sceneType) options.type = sceneType;
    const params = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/cdp_scene/page', { params });
  }

  async getScene(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_scene/get/${uuid}`);
  }

  async createScene(
    projectUuid: string,
    name: string,
    sceneType: string,
    subtype = 100,
    config?: Record<string, any>,
  ): Promise<Record<string, any>> {
    const data = {
      context:{},
      data: {
        projectUuid,
        name,
        type: sceneType,
        subtype,
        config: config ?? {},
      },
    };
    return this.session.post('/api/cdp/cdp_scene/create', { data });
  }

  async updateScene(
    uuid: string,
    name?: string,
    config?: Record<string, any>,
    isPublished?: boolean,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { context:{}, data: { uuid } };
    if (name) data.data.name = name;
    if (config) data.data.config = config;
    if (isPublished !== undefined) data.data.isPublished = isPublished ? 1 : 0;
    return this.session.post('/api/cdp/cdp_scene/update', { data });
  }

  async deleteScene(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_scene/delete/${uuid}`);
  }

  async batchDeleteScenes(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_scene/extend/batchDelete', { data: uuids });
  }

  async copyScene(
    uuid: string,
    name: string,
    projectUuid?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { srcSceneUuid: uuid, name };
    if (projectUuid) data.projectUuid = projectUuid;
    return this.session.post('/api/cdp/cdp_scene/copy', { data });
  }

  async publishScene(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_scene/publish/${uuid}`);
  }

  async getScenePreviewUrl(uuid: string): Promise<string> {
    const result = await this.session.get(`/api/cdp/cdp_scene/preview/${uuid}`);
    return result?.data ?? '';
  }

  async getSceneElements(uuid: string): Promise<Record<string, any>[]> {
    const result = await this.session.get('/api/cdp/element/page', {
      params: { sceneUuid: uuid },
    });
    return result?.data?.records ?? [];
  }

  async createElement(
    sceneUuid: string,
    elementType: string,
    config: Record<string, any>,
    name?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        sceneUuid,
        type: elementType,
        config,
        name,
      },
    };
    return this.session.post('/api/cdp/element', { data });
  }

  async updateElement(
    uuid: string,
    config: Record<string, any>,
    name?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid, config } };
    if (name) data.data.name = name;
    return this.session.put('/api/cdp/element', { data });
  }

  async deleteElement(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/element/${uuid}`);
  }

  async batchCreateElements(
    sceneUuid: string,
    elements: Record<string, any>[],
  ): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/element/batch', { data: { sceneUuid, elements } });
  }

  async getSceneDatasetRefs(uuid: string): Promise<Record<string, any>[]> {
    const result = await this.session.get(`/api/cdp/cdp_scene/datasetRefs/${uuid}`);
    return result?.data ?? [];
  }

  async getSceneDependencies(uuid: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/sys_project/extend/dependencies', { data: { uuid } });
  }

  /** Export scene as Blob. */
  async exportScene(uuid: string, withSceneDep = false): Promise<Blob> {
    const formData = new FormData();
    formData.append('uuid', uuid);
    formData.append('withSceneDep', String(withSceneDep));
    return this.session.request('POST', 'api/cdp/cdp_scene/extend/export', {
      data: formData,
      responseType: 'blob',
    });
  }

  async importScene(
    projectUuid: string,
    file: File | Blob,
    name?: string,
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, any> = { projectUuid };
    if (name) params.name = name;
    return this.session.post('api/cdp/cdp_scene/extend/uploadScene', { data: formData, params, headers: {} });
  }

  listSceneTypes(): Record<string, any>[] {
    return [
      { id: '1', name: '2D Scene' },
      { id: '2', name: '3D Scene' },
      { id: '3', name: 'Mixed Scene' },
    ];
  }

  getSceneTypes(): Record<string, string> {
    return {
      '1': '2D',
      '2': '3D',
      '3': 'Mixed',
      '10': 'Normal',
      '11': 'Template',
    };
  }

  // --- Extended scene APIs ---

  async uploadScene(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/cdp_scene/extend/uploadScene', { data: formData, params, headers: {} });
  }

  async batchUploadScenes(files: File[] | Blob[], params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    return this.session.post('/api/cdp/cdp_scene/extend/batchUploadScene', { data: formData, params, headers: {} });
  }

  async exportSceneExtend(params?: Record<string, any>): Promise<Blob> {
    return this.session.request('GET', '/api/cdp/cdp_scene/extend/export', {
      params,
      responseType: 'blob',
    });
  }

  async copySceneEditor(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/copyScene', { data });
  }

  async getSchema(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/dataset/schema/${uuid}`);
  }

  async getSchemas(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/dataset/schemas', { data });
  }

  // --- Scene list / share extended APIs ---

  async listProjectScene(projectUuid: string, page = 1, size = 1000): Promise<Record<string, any>> {
    const params = {
      page,
      size,
      options: JSON.stringify({ where: { projectUuid, subtype: ['notIn', '10,11'] } }),
    };
    return this.session.get('/api/cdp/cdp_scene/extend/page2', { params });
  }

  async getProjectSceneList(projectUuid: string): Promise<Record<string, any>> {
    const params = {
      options: JSON.stringify({ where: { projectUuid, subtype: ['ne', '10'] } }),
    };
    return this.session.get('/api/cdp/cdp_scene/extend/list', { params });
  }

  async batchUpdateScenes(datas: Record<string, any>[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_scene/batchUpdate', { data: { context: {}, data: datas } });
  }

  async getPluginVersionId(pluginUuid: string, vectorVersionUuid?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = { pluginUuid };
    if (vectorVersionUuid) params.vectorVersionUuid = vectorVersionUuid;
    return this.session.get('/api/cdp/cdp_scene/extend/getPluginVersionId', { params });
  }

  async getSceneDeviceUuids(sceneUuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_scene/extend/${sceneUuid}/deviceUuids`);
  }

  async shareScene(data: Record<string, any>): Promise<Record<string, any>> {
    const payload = { ...data };
    if (payload.visitPassword) {
      const publicKey = this.session.publicKey ?? (await this.session.getPublicKey());
      payload.visitPassword = this.session.encryptPassword(publicKey, payload.visitPassword);
    }
    return this.session.post('/api/cdp/cdp_scene/extend/share', { data: payload });
  }

  async getShareRecords(sceneUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_scene/extend/share/records', { params: { sceneUuid } });
  }

  async getLastEffectiveShare(sceneUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_scene/extend/share/lastEffectiveShare', { params: { sceneUuid } });
  }

  async disableShare(id: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_scene/extend/share/${id}/disable`);
  }
}
